import {
  cellToBoundary,
  cellToLatLng,
  gridDisk,
  latLngToCell,
} from 'h3-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { loadGoogleMaps } from '@/lib/googleMaps';
import { GSM_MAP_STYLE } from '@/lib/googleMapsStyle';

const H3_RESOLUTION = 8;

// Fallback center when no hub is selected (or a hub has invalid coords).
// Tugu Monas — the canonical "center of Jakarta" reference for the city.
const JAKARTA_CENTER = { lat: -6.1754, lng: 106.8272 };
const JAKARTA_ZOOM = 11; // City-level view (~30km radius)
const HUB_ZOOM = 12; // Neighborhood view when a hub is in focus

const isValidCoord = (lat: unknown, lng: unknown): boolean => {
  // Coerce defensively — Drizzle numeric columns can serialize as strings
  // depending on the path. `Number.isFinite` is strict and rejects strings,
  // so we coerce first and then validate the resulting numeric range.
  const nLat = typeof lat === 'number' ? lat : Number(lat);
  const nLng = typeof lng === 'number' ? lng : Number(lng);
  return (
    Number.isFinite(nLat) &&
    Number.isFinite(nLng) &&
    nLat >= -90 &&
    nLat <= 90 &&
    nLng >= -180 &&
    nLng <= 180 &&
    // Reject the (0,0) placeholder that would otherwise center the map
    // on the Atlantic off the coast of Africa.
    !(nLat === 0 && nLng === 0)
  );
};

// Material-style teardrop pin. Anchor at (0,0) = the tip, so the marker's
// point lands exactly on the hub's lat/lng. The bulb sits above the tip.
//
// Using a different shape primitive (teardrop) than the cell polygons
// (hexagons) means the hub pin stays visually distinct regardless of which
// zone color happens to surround it.
const HUB_PIN_PATH =
  'M 0,0 C -6,-8 -12,-14 -12,-22 C -12,-28 -6,-32 0,-32 C 6,-32 12,-28 12,-22 C 12,-14 6,-8 0,0 z';

// Builds the inner HTML for the hub banner. Inline styles so the banner is
// self-contained — Google Maps OverlayView mounts this outside the React
// tree, so Tailwind class names don't apply. Single line: hub name only.
// The code lives in the top-bar picker; repeating it on the pin is noise.
const buildHubBannerHTML = (hub: ZoneMapHub): string => {
  const safe = (s: string) =>
    s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `
    <div style="
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      pointer-events: none;
      transform: translate(-50%, calc(-100% - 44px));
      font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    ">
      <div style="
        background: #ffffff;
        border: 1px solid rgba(23,23,23,0.10);
        border-radius: 4px;
        padding: 4px 10px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        white-space: nowrap;
        line-height: 1.25;
        font-size: 11.5px;
        font-weight: 600;
        color: #171717;
        letter-spacing: -0.01em;
      ">${safe(hub.name)}</div>
      <div style="
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #ffffff;
        margin-top: -1px;
        filter: drop-shadow(0 1px 0 rgba(23,23,23,0.10));
      "></div>
    </div>
  `;
};

// Lazily constructs the OverlayView subclass once the Google Maps SDK is
// loaded. Returns a factory that creates an instance attached to a
// specific position.
const makeHubBannerFactory = (maps: any) => {
  class HubBanner extends maps.OverlayView {
    position: any;
    div: HTMLDivElement | null = null;
    constructor(position: any, html: string) {
      super();
      this.position = position;
      this._pendingHtml = html;
    }
    _pendingHtml: string;
    onAdd() {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.willChange = 'transform';
      div.innerHTML = this._pendingHtml;
      this.div = div;
      const panes = this.getPanes();
      // floatPane sits above markers but below the InfoWindow layer —
      // exactly where a label/callout belongs.
      panes.floatPane.appendChild(div);
    }
    draw() {
      const projection = this.getProjection();
      if (!projection || !this.div) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }
    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
      }
      this.div = null;
    }
    setPosition(position: any) {
      this.position = position;
      this.draw();
    }
    setHTML(html: string) {
      if (this.div) this.div.innerHTML = html;
      else this._pendingHtml = html;
    }
  }
  return (position: any, html: string) => new HubBanner(position, html);
};

/**
 * Mounts a caller-owned DOM node at a LatLng and keeps it pinned there while
 * the map pans and zooms. Unlike `HubBanner` (which owns its innerHTML), the
 * node stays empty here so React can `createPortal` real components into it —
 * that's what makes the driver badge clickable and its popover stateful.
 *
 * The node is centred on its anchor point via a transform, so the caller
 * styles the chip itself and never deals with pixel maths.
 */
const makeDomOverlayFactory = (maps: any) => {
  class DomOverlay extends maps.OverlayView {
    position: any;
    el: HTMLDivElement;
    constructor(position: any, el: HTMLDivElement) {
      super();
      this.position = position;
      this.el = el;
    }
    onAdd() {
      this.getPanes().floatPane.appendChild(this.el);
    }
    draw() {
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.el.style.left = `${point.x}px`;
      this.el.style.top = `${point.y}px`;
    }
    onRemove() {
      if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
    }
    setPosition(position: any) {
      this.position = position;
      this.draw();
    }
  }
  return (position: any, el: HTMLDivElement) => new DomOverlay(position, el);
};

// The SDK is a singleton, so the subclass is built once per page load rather
// than on every effect run.
let domOverlayFactory:
  | ((position: any, el: HTMLDivElement) => any)
  | null = null;
const getDomOverlayFactory = (maps: any) => {
  if (!domOverlayFactory) domOverlayFactory = makeDomOverlayFactory(maps);
  return domOverlayFactory;
};

/**
 * Brand stamped onto every DOM `click` that starts inside an overlay chip.
 * Google Maps delivers its map 'click' ~250ms late (double-click
 * disambiguation) carrying the SAME DOM event object — by then React may have
 * unmounted the exact node that was clicked (e.g. the popover's × button), so
 * a `host.contains(target)` check is flaky. A property on the event itself
 * survives any DOM churn.
 */
const OVERLAY_CLICK_FLAG = '__zoneOverlayClick';

/**
 * Creates the host node for a DOM overlay. Interaction events are stopped
 * here so a click, drag, or scroll inside a chip never reaches the map div —
 * without this, dragging a popover's scrollbar pans the map and a wheel over
 * the roster zooms it.
 *
 * `click` is deliberately NOT stopped: React 19 delegates its listeners at
 * the app root, so cutting propagation at this node would kill the chip's own
 * onClick. Instead the click is stamped (capture phase, so an inner
 * stopPropagation can't skip it) and the map's click handler drops stamped
 * events — see OVERLAY_CLICK_FLAG.
 */
const createOverlayHost = (): HTMLDivElement => {
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.transform = 'translate(-50%, -50%)';
  el.style.willChange = 'transform';
  for (const type of [
    'mousedown',
    'touchstart',
    'dblclick',
    'wheel',
    'contextmenu',
  ]) {
    el.addEventListener(type, (e) => e.stopPropagation());
  }
  // Maps' delayed map-click may hand back the click, the mouseup, or the
  // pointerup as `domEvent` depending on version/input — stamp all three.
  for (const type of ['click', 'mouseup', 'pointerup']) {
    el.addEventListener(
      type,
      (e) => {
        (e as any)[OVERLAY_CLICK_FLAG] = true;
      },
      true
    );
  }
  return el;
};

/**
 * Anchor point for a zone's floating chip: the centre of the cell nearest the
 * zone's mean centre. A plain average can land outside a concave or split
 * zone, which reads as a badge hovering over someone else's territory.
 */
const zoneAnchor = (cells: string[]): { lat: number; lng: number } | null => {
  if (cells.length === 0) return null;
  const centers = cells.map((c) => cellToLatLng(c));
  let sumLat = 0;
  let sumLng = 0;
  for (const [lat, lng] of centers) {
    sumLat += lat;
    sumLng += lng;
  }
  const avgLat = sumLat / centers.length;
  const avgLng = sumLng / centers.length;
  let best = centers[0];
  let bestDist = Infinity;
  for (const [lat, lng] of centers) {
    const dist = (lat - avgLat) ** 2 + (lng - avgLng) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = [lat, lng];
    }
  }
  return { lat: best[0], lng: best[1] };
};

export interface ZoneOverlay {
  id: string;
  code: string;
  color: string;
  cells: string[];
  /** True for the zone the user is editing — drawn fully opaque. */
  active?: boolean;
}

/**
 * A dispatch barrier (toll road / arterial / river) as a res-10 H3 chain.
 * Display-only: rendered as ONE merged polygon per barrier (not one polygon
 * per cell — a barrier has hundreds of cells) below the zone layer, and
 * never clickable so zone-cell editing works right through it.
 */
export interface BarrierOverlay {
  id: string;
  code: string;
  h3Indexes: string[];
}

// Dark neutral so barriers read as "infrastructure", distinct from any
// zone's configurable color. Drawn as smoothed polylines through the cell
// centres — a line reads like the road it represents, unlike a hex ribbon.
const BARRIER_LINE_STYLE = {
  strokeColor: '#1f2937',
  strokeOpacity: 0.55,
  strokeWeight: 3,
  // Below zone cells (100/200) — barriers are context, zones are content.
  zIndex: 50,
  clickable: false,
} as const;

/** Chaikin smoothing passes on barrier lines. Each pass cuts every corner
 * (vertex → two points at 1/4 and 3/4 of the adjoining edges), rounding
 * the cell-centre zigzag into a road-like curve. */
const BARRIER_SMOOTHING_PASSES = 2;

/** One Chaikin pass over an OPEN path ([lat, lng] pairs), endpoints kept. */
const chaikinOpen = (pts: number[][]): number[][] => {
  if (pts.length < 3) return pts;
  const out: number[][] = [pts[0]];
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[i + 1];
    out.push(
      [ax * 0.75 + bx * 0.25, ay * 0.75 + by * 0.25],
      [ax * 0.25 + bx * 0.75, ay * 0.25 + by * 0.75]
    );
  }
  out.push(pts[pts.length - 1]);
  return out;
};

/**
 * Order a barrier's (unordered) cell set into drawable strands.
 *
 * Cells are stored as a set, so a line needs paths reconstructing: build
 * the grid-adjacency graph, start walks at endpoints (fewest neighbours),
 * and at forks continue in the straightest direction — that keeps a walk
 * on one carriageway instead of zigzagging between parallel strands. The
 * second carriageway's leftover cells become their own strand, which is
 * exactly how the real road looks. Blobby areas (interchanges) fall apart
 * into short strokes; single leftover cells are dropped.
 */
const barrierStrands = (cells: string[]): string[][] => {
  const inBarrier = new Set(cells);
  const neighborsOf = new Map<string, string[]>();
  for (const c of cells) {
    neighborsOf.set(
      c,
      gridDisk(c, 1).filter((n) => n !== c && inBarrier.has(n))
    );
  }
  // Endpoint-first seeding so open chains trace end-to-end.
  const seeds = [...cells].sort(
    (a, b) => neighborsOf.get(a)!.length - neighborsOf.get(b)!.length
  );
  const visited = new Set<string>();
  const strands: string[][] = [];
  for (const seed of seeds) {
    if (visited.has(seed)) continue;
    const strand = [seed];
    visited.add(seed);
    let prev: string | null = null;
    let cur = seed;
    for (;;) {
      const nexts = neighborsOf.get(cur)!.filter((n) => !visited.has(n));
      if (nexts.length === 0) break;
      let next = nexts[0];
      if (prev !== null && nexts.length > 1) {
        const [plat, plng] = cellToLatLng(prev);
        const [clat, clng] = cellToLatLng(cur);
        const dLat = clat - plat;
        const dLng = clng - plng;
        let best = -Infinity;
        for (const cand of nexts) {
          const [nlat, nlng] = cellToLatLng(cand);
          const vLat = nlat - clat;
          const vLng = nlng - clng;
          const cos =
            (dLat * vLat + dLng * vLng) /
            (Math.hypot(dLat, dLng) * Math.hypot(vLat, vLng) || 1);
          if (cos > best) {
            best = cos;
            next = cand;
          }
        }
      }
      strand.push(next);
      visited.add(next);
      prev = cur;
      cur = next;
    }
    if (strand.length >= 2) strands.push(strand);
  }
  return strands;
};

/** Cell strand → smoothed Google Maps polyline path. */
const strandToPath = (
  strand: string[]
): Array<{ lat: number; lng: number }> => {
  let pts = strand.map((c) => {
    const [lat, lng] = cellToLatLng(c);
    return [lat, lng];
  });
  for (let i = 0; i < BARRIER_SMOOTHING_PASSES; i++) pts = chaikinOpen(pts);
  return pts.map(([lat, lng]) => ({ lat, lng }));
};

export interface ZoneMapHub {
  id: string;
  code: string;
  name: string;
  latitude: number;
  longitude: number;
  /** True for the hub whose zones are being edited (highlighted pin). */
  active?: boolean;
}

export interface ZoneMapProps {
  /**
   * The hub currently being edited. When omitted (no hub selected yet),
   * the map defaults to a Jakarta city-level view with no hub pin so the
   * surface is still visible and the user has a sense of place.
   */
  hub?: ZoneMapHub;
  /** Additional hubs to plot as muted pins (boundary verification). */
  otherHubs?: ZoneMapHub[];
  overlays: ZoneOverlay[];
  /** Dispatch barriers to draw as a muted background layer. */
  barriers?: BarrierOverlay[];
  /**
   * Called when the user clicks the map. Receives the H3 cell at the click
   * point. The parent decides whether to add to current zone, remove from
   * current zone, or surface a conflict.
   */
  onCellClick: (h3Index: string) => void;
  /** ID of the zone currently selected for editing. */
  activeZoneId: string | null;
  /**
   * Zones that get a floating chip anchored at their centroid — the driver
   * badge layer. ZoneMap owns anchoring and map-gesture isolation; the caller
   * owns what the chip looks like. Zones with no cells are skipped: there is
   * nowhere on the map to put them.
   */
  badgeZoneIds?: string[];
  renderZoneBadge?: (zoneId: string) => React.ReactNode;
  /**
   * Zone whose chip has an open panel. Rendered in a separate, higher overlay
   * so the panel is never covered by a neighbouring chip.
   */
  popoverZoneId?: string | null;
  renderZonePopover?: (zoneId: string) => React.ReactNode;
  /**
   * Optional explicit height in pixels. Omit to fill the parent — required
   * when the map is dropped inside a flex/absolute container where the
   * parent owns the height. Passing a value here forces the container to
   * that pixel size, which de-syncs Google Maps' center from what the user
   * actually sees.
   */
  height?: number;
}

const ZoneMap: React.FC<ZoneMapProps> = ({
  hub,
  otherHubs = [],
  overlays,
  barriers = [],
  onCellClick,
  activeZoneId,
  badgeZoneIds,
  renderZoneBadge,
  popoverZoneId = null,
  renderZonePopover,
  height,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const hubMarkerRef = useRef<any>(null);
  // Custom HTML banner that floats above the hub pin showing the hub name.
  // Lives outside the React tree (Google Maps OverlayView manages its own
  // DOM lifecycle), so we hold the instance in a ref.
  const hubBannerRef = useRef<any>(null);
  const otherMarkersRef = useRef<any[]>([]);
  // Tracks which hub the map was last centered/zoomed onto, so we DON'T
  // override the user's manual zoom/pan on every re-render. We only
  // recenter when the active hub id actually transitions.
  const lastAppliedHubIdRef = useRef<string | null>(null);
  // h3 → google.maps.Polygon (so we can update fill on a single cell)
  const cellPolysRef = useRef<Map<string, any>>(new Map());
  // barrier id → google.maps.Polyline[] (one per strand of the barrier)
  const barrierPolysRef = useRef<Map<string, any[]>>(new Map());
  // h3 → zoneId (reverse lookup for click handling)
  const cellOwnerRef = useRef<Map<string, string>>(new Map());
  // zoneId → { overlay instance, host node } for the badge layer, plus the
  // single higher-stacked host for the open popover.
  const badgeOverlaysRef = useRef<
    Map<string, { overlay: any; el: HTMLDivElement }>
  >(new Map());
  const popoverOverlayRef = useRef<{
    zoneId: string;
    overlay: any;
    el: HTMLDivElement;
  } | null>(null);
  // Every live host node — the map click handler uses this to tell "clicked a
  // chip" apart from "clicked the map", since both surface the same event.
  const overlayHostsRef = useRef<Set<HTMLElement>>(new Set());
  // Host nodes mirrored into state so React can portal into them. Kept as a
  // fresh Map identity on every structural change so the render updates.
  const [badgeHosts, setBadgeHosts] = useState<Map<string, HTMLDivElement>>(
    new Map()
  );
  const [popoverHost, setPopoverHost] = useState<{
    zoneId: string;
    el: HTMLDivElement;
  } | null>(null);
  // Latest props mirrored in refs so the map click handler always sees
  // current state without rebinding the listener.
  const onCellClickRef = useRef(onCellClick);
  const activeZoneIdRef = useRef(activeZoneId);
  // Latest hub mirrored in a ref so the async init effect (which only
  // captures props from its first run) can read the up-to-date hub when
  // Google Maps finishes loading. Without this, if hubs finish loading
  // BEFORE the SDK does, the map opens at Jakarta and never moves.
  const hubRef = useRef(hub);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Flip true once the map instance exists — used as a dep on the
  // re-center effect so the initial hub centering fires reliably, even
  // when hubs resolve before the Google Maps SDK does.
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    onCellClickRef.current = onCellClick;
  }, [onCellClick]);
  useEffect(() => {
    activeZoneIdRef.current = activeZoneId;
  }, [activeZoneId]);
  useEffect(() => {
    hubRef.current = hub;
  }, [hub]);

  // Initialize map exactly once per mount. We deliberately ALWAYS start at
  // Jakarta — never at the hub's coords — so the initial viewport is a
  // known fixed state regardless of when hubs finish fetching. The hub
  // marker drop + smooth pan to the hub happens via the re-center effect
  // below, which gives ops a brief "we're moving you to HUB_X" moment
  // instead of an opaque jump.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: JAKARTA_CENTER,
          zoom: JAKARTA_ZOOM,
          styles: GSM_MAP_STYLE,
          disableDefaultUI: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        mapRef.current = map;
        if (process.env.NODE_ENV !== 'production') {
          // Dev-only handle so tests/debugging can drive the viewport.
          (window as any).__zoneMap = map;
        }

        // Map-level click → resolve to H3 cell and bubble up. The parent's
        // handler decides whether this lands inside an existing cell or
        // creates a new one — but the resolution is the same h3 index
        // either way (cells are uniquely determined by the click latlng).
        map.addListener('click', (e: any) => {
          // Chips live inside the map's own DOM, so a click on one also
          // registers as a map click — and Maps delivers it ~250ms late
          // (double-click disambiguation). Primary guard: the stamp the
          // overlay host put on the DOM event (immune to React unmounting
          // the clicked node in the meantime). The contains() walk stays as
          // a fallback for events that somehow skipped the host listener.
          if (e.domEvent?.[OVERLAY_CLICK_FLAG]) return;
          const target = e.domEvent?.target as Node | undefined;
          if (target) {
            for (const host of overlayHostsRef.current) {
              if (host === target || host.contains(target)) return;
            }
          }
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          const h3 = latLngToCell(lat, lng, H3_RESOLUTION);
          onCellClickRef.current?.(h3);
        });

        setLoading(false);
        // Signal that the map is ready so the re-center effect can pan
        // to whatever hub is currently selected.
        setMapReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load Google Maps'
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Map init runs once. Hub re-pin handled in a separate effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-center + re-pin ONLY when the active hub id transitions. This
  // guarantees we don't snap the user's manual zoom or pan back on every
  // re-render — Google Maps owns viewport state once initialized; we only
  // intervene when ops explicitly switches hubs.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    const currentHubId = hub?.id ?? null;
    const lastHubId = lastAppliedHubIdRef.current;

    // Coerce lat/lng to numbers — Drizzle numeric columns can serialize as
    // strings, and Google Maps silently ignores string LatLngLiterals
    // (the map would stay at the Jakarta fallback and never pan to the hub).
    const hubLat = hub ? Number(hub.latitude) : NaN;
    const hubLng = hub ? Number(hub.longitude) : NaN;
    const hubHasCoords = hub && isValidCoord(hubLat, hubLng);

    // Banner content sync — cheap (innerHTML swap) and doesn't touch
    // viewport, so we keep it up to date on every rename without gating
    // on id transition.
    if (hubHasCoords && hub && hubBannerRef.current) {
      hubBannerRef.current.setHTML(buildHubBannerHTML(hub));
    }
    if (hubHasCoords && hub && hubMarkerRef.current) {
      hubMarkerRef.current.setTitle(`${hub.code} · ${hub.name}`);
    }

    // Bail if the id didn't change — same hub, no viewport update.
    if (currentHubId === lastHubId) return;

    if (hubHasCoords && hub) {
      const center = { lat: hubLat, lng: hubLng };
      const latLng = new maps.LatLng(hubLat, hubLng);
      // Use setCenter (instant) instead of panTo (animated) so there's no
      // animation race with setZoom that could leave the camera in an
      // intermediate state. setOptions applies both atomically.
      map.setOptions({ center, zoom: HUB_ZOOM });
      if (process.env.NODE_ENV !== 'production') {
        // Dev-only log so you can verify the coords reaching the SDK.
        // eslint-disable-next-line no-console
        console.log('[ZoneMap] centering on hub', {
          code: hub.code,
          lat: hubLat,
          lng: hubLng,
          raw: { lat: hub.latitude, lng: hub.longitude },
        });
      }

      // Marker (clean teardrop, no in-pin label — the banner above does the labeling)
      if (hubMarkerRef.current) {
        hubMarkerRef.current.setPosition(center);
        hubMarkerRef.current.setMap(map);
      } else {
        hubMarkerRef.current = new maps.Marker({
          position: center,
          map,
          title: `${hub.code} · ${hub.name}`,
          icon: {
            path: HUB_PIN_PATH,
            scale: 1.1,
            fillColor: '#5E2AAC',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2.5,
            anchor: new maps.Point(0, 0),
          },
          zIndex: 1000,
        });
      }

      // Banner (floating HTML callout above the pin)
      if (hubBannerRef.current) {
        hubBannerRef.current.setPosition(latLng);
        hubBannerRef.current.setMap(map);
        hubBannerRef.current.setHTML(buildHubBannerHTML(hub));
      } else {
        const factory = makeHubBannerFactory(maps);
        hubBannerRef.current = factory(latLng, buildHubBannerHTML(hub));
        hubBannerRef.current.setMap(map);
      }
    } else {
      // No valid hub → reset to Jakarta city view atomically.
      map.setOptions({ center: JAKARTA_CENTER, zoom: JAKARTA_ZOOM });
      if (hubMarkerRef.current) {
        hubMarkerRef.current.setMap(null);
        hubMarkerRef.current = null;
      }
      if (hubBannerRef.current) {
        hubBannerRef.current.setMap(null);
        hubBannerRef.current = null;
      }
      if (process.env.NODE_ENV !== 'production' && hub) {
        // Hub was set but rejected by isValidCoord — likely bad data.
        // eslint-disable-next-line no-console
        console.warn('[ZoneMap] hub coords failed validation', {
          code: hub.code,
          lat: hub.latitude,
          lng: hub.longitude,
          types: { lat: typeof hub.latitude, lng: typeof hub.longitude },
        });
      }
    }

    lastAppliedHubIdRef.current = currentHubId;
    // `mapReady` is in deps so this effect fires once the map instance
    // exists — guarantees the initial hub centering even when hubs
    // resolved before the SDK did. The other deps are primitives from the
    // hub (not the object itself) because the parent creates a fresh hub
    // literal on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, hub?.id, hub?.code, hub?.name, hub?.latitude, hub?.longitude]);

  // Other-hub markers (muted).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    otherMarkersRef.current.forEach((m) => m.setMap(null));
    otherMarkersRef.current = otherHubs
      .map((h) => {
        const lat = Number(h.latitude);
        const lng = Number(h.longitude);
        if (!isValidCoord(lat, lng)) return null;
        return new maps.Marker({
          position: { lat, lng },
          map,
          title: `${h.code} · ${h.name}`,
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#9ca3af',
            fillOpacity: 0.85,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: 500,
        });
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
  }, [mapReady, otherHubs]);

  // Re-render H3 polygons whenever overlays change. We diff cell-by-cell so
  // a single cell add/remove doesn't tear down 500 unrelated polygons.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    const polys = cellPolysRef.current;
    const owners = cellOwnerRef.current;

    // Build the desired state: h3 → { color, active, zoneId }
    const desired = new Map<
      string,
      { color: string; active: boolean; zoneId: string }
    >();
    for (const overlay of overlays) {
      for (const cell of overlay.cells) {
        // If the same cell appears in multiple overlays (shouldn't, per
        // disjoint invariant), the last write wins.
        desired.set(cell, {
          color: overlay.color,
          active: !!overlay.active,
          zoneId: overlay.id,
        });
      }
    }

    // Drop polygons that no longer have a desired cell.
    polys.forEach((poly, cell) => {
      if (!desired.has(cell)) {
        poly.setMap(null);
        polys.delete(cell);
        owners.delete(cell);
      }
    });

    // Add or restyle polygons for desired cells.
    desired.forEach(({ color, active, zoneId }, cell) => {
      const existing = polys.get(cell);
      // Inactive zones are deliberately faint so the active zone always
      // wins the visual hierarchy even when the inactive color is
      // higher-chroma (e.g., magenta reads heavier than purple at equal
      // opacity).
      const opts = {
        strokeColor: color,
        strokeOpacity: active ? 1 : 0.5,
        strokeWeight: active ? 2 : 1,
        fillColor: color,
        fillOpacity: active ? 0.4 : 0.1,
        zIndex: active ? 200 : 100,
      };
      if (existing) {
        existing.setOptions(opts);
      } else {
        const boundary = cellToBoundary(cell, false).map(([lat, lng]) => ({
          lat,
          lng,
        }));
        // clickable:false drops per-frame hit-testing during zoom — clicks
        // are resolved at the map level via latLngToCell, so per-polygon
        // listeners are redundant. With 50+ cells this is the single
        // biggest perf win on pan/zoom.
        const poly = new maps.Polygon({
          paths: boundary,
          ...opts,
          clickable: false,
          map,
        });
        polys.set(cell, poly);
      }
      owners.set(cell, zoneId);
    });
  }, [mapReady, overlays]);

  // Dispatch-barrier layer: smoothed polylines through each barrier's cell
  // centres (strand reconstruction in barrierStrands). Rebuilt (not diffed)
  // on change: the list is tiny and effectively static per session.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    const polys = barrierPolysRef.current;
    polys.forEach((lines) => lines.forEach((l) => l.setMap(null)));
    polys.clear();

    for (const barrier of barriers) {
      if (barrier.h3Indexes.length === 0) continue;
      const lines = barrierStrands(barrier.h3Indexes).map(
        (strand) =>
          new maps.Polyline({
            path: strandToPath(strand),
            ...BARRIER_LINE_STYLE,
            map,
          })
      );
      polys.set(barrier.id, lines);
    }
  }, [mapReady, barriers]);

  // ── Floating chip layer (driver badges + the open roster popover) ─────

  // Anchor per badge zone, derived from the cells already on screen. Zones
  // with no cells drop out (nowhere to anchor them).
  //
  // Keyed on the cells array itself: painting a cell rebuilds `overlays` but
  // reuses every untouched zone's array, so only the edited zone recomputes.
  // Without the cache, every click walked all cells of all zones.
  const anchorCacheRef = useRef<WeakMap<string[], { lat: number; lng: number }>>(
    new WeakMap()
  );
  const badgeAnchors = useMemo(() => {
    const out = new Map<string, { lat: number; lng: number }>();
    if (!badgeZoneIds || badgeZoneIds.length === 0) return out;
    const cache = anchorCacheRef.current;
    const wanted = new Set(badgeZoneIds);
    for (const overlay of overlays) {
      if (!wanted.has(overlay.id)) continue;
      const cached = cache.get(overlay.cells);
      if (cached) {
        out.set(overlay.id, cached);
        continue;
      }
      const anchor = zoneAnchor(overlay.cells);
      if (anchor) {
        cache.set(overlay.cells, anchor);
        out.set(overlay.id, anchor);
      }
    }
    return out;
  }, [overlays, badgeZoneIds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const maps = (window as any).google?.maps;
    if (!maps) return;

    const store = badgeOverlaysRef.current;
    const hosts = overlayHostsRef.current;
    let structureChanged = false;

    store.forEach((entry, zoneId) => {
      if (!badgeAnchors.has(zoneId)) {
        entry.overlay.setMap(null);
        hosts.delete(entry.el);
        store.delete(zoneId);
        structureChanged = true;
      }
    });

    const factory = getDomOverlayFactory(maps);
    badgeAnchors.forEach((anchor, zoneId) => {
      const latLng = new maps.LatLng(anchor.lat, anchor.lng);
      const existing = store.get(zoneId);
      if (existing) {
        // Cheap re-anchor: the zone's cells changed, so its centroid moved.
        existing.overlay.setPosition(latLng);
        return;
      }
      const el = createOverlayHost();
      el.style.zIndex = '10';
      const overlay = factory(latLng, el);
      overlay.setMap(map);
      store.set(zoneId, { overlay, el });
      hosts.add(el);
      structureChanged = true;
    });

    if (structureChanged) {
      setBadgeHosts(
        new Map(Array.from(store, ([zoneId, entry]) => [zoneId, entry.el]))
      );
    }
  }, [mapReady, badgeAnchors]);

  // The popover gets its own overlay, stacked above every badge, so a panel
  // opened on one zone is never clipped by a chip on the zone next door.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const maps = (window as any).google?.maps;
    if (!maps) return;

    const hosts = overlayHostsRef.current;
    const current = popoverOverlayRef.current;
    const zoneId = popoverZoneId;
    const anchor = zoneId ? badgeAnchors.get(zoneId) : undefined;

    if (!zoneId || !anchor) {
      if (current) {
        current.overlay.setMap(null);
        hosts.delete(current.el);
        popoverOverlayRef.current = null;
        setPopoverHost(null);
      }
      return;
    }

    const latLng = new maps.LatLng(anchor.lat, anchor.lng);
    if (current && current.zoneId === zoneId) {
      current.overlay.setPosition(latLng);
      return;
    }
    if (current) {
      current.overlay.setMap(null);
      hosts.delete(current.el);
    }
    const el = createOverlayHost();
    el.style.zIndex = '60';
    const overlay = getDomOverlayFactory(maps)(latLng, el);
    overlay.setMap(map);
    popoverOverlayRef.current = { zoneId, overlay, el };
    hosts.add(el);
    setPopoverHost({ zoneId, el });
  }, [mapReady, popoverZoneId, badgeAnchors]);

  // Tear down everything on unmount. Capture ref values locally so the
  // cleanup operates on the Maps that existed at mount time
  // (eslint react-hooks/exhaustive-deps).
  useEffect(() => {
    const polys = cellPolysRef.current;
    const owners = cellOwnerRef.current;
    const barrierPolys = barrierPolysRef.current;
    const badgeOverlays = badgeOverlaysRef.current;
    const hosts = overlayHostsRef.current;
    return () => {
      badgeOverlays.forEach((entry) => entry.overlay.setMap(null));
      badgeOverlays.clear();
      if (popoverOverlayRef.current) {
        popoverOverlayRef.current.overlay.setMap(null);
        popoverOverlayRef.current = null;
      }
      hosts.clear();
      polys.forEach((p) => p.setMap(null));
      polys.clear();
      owners.clear();
      barrierPolys.forEach((lines) => lines.forEach((l) => l.setMap(null)));
      barrierPolys.clear();
      otherMarkersRef.current.forEach((m) => m.setMap(null));
      otherMarkersRef.current = [];
      if (hubMarkerRef.current) hubMarkerRef.current.setMap(null);
      hubMarkerRef.current = null;
      if (hubBannerRef.current) hubBannerRef.current.setMap(null);
      hubBannerRef.current = null;
      mapRef.current = null;
    };
  }, []);

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-weak-50"
      style={height !== undefined ? { height } : undefined}
    >
      {error ? (
        <div className="flex h-full items-center justify-center px-3 text-center text-[12px] text-error-base">
          <i className="ri-error-warning-line mr-1 text-[14px]" />
          Map unavailable. {error}
        </div>
      ) : (
        <>
          <div ref={containerRef} className="h-full w-full" />
          {renderZoneBadge &&
            Array.from(badgeHosts, ([zoneId, el]) =>
              createPortal(renderZoneBadge(zoneId), el, `badge-${zoneId}`)
            )}
          {renderZonePopover &&
            popoverHost &&
            createPortal(
              renderZonePopover(popoverHost.zoneId),
              popoverHost.el,
              `popover-${popoverHost.zoneId}`
            )}
          {loading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-bg-white-0/70 text-[12px] text-text-sub-600">
              Loading map…
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ZoneMap;
